/**
 * Server / Node: prefers `SENTRY_DSN`, falls back to `NEXT_PUBLIC_SENTRY_DSN` (e.g. when only public is set).
 * Does not run in the browser.
 */
export function getSentryDsn(): string | undefined {
  const dsn =
    process.env.SENTRY_DSN?.trim() ||
    process.env.NEXT_PUBLIC_SENTRY_DSN?.trim()
  return dsn || undefined
}

/**
 * Browser bundle: use `NEXT_PUBLIC_SENTRY_DSN` only (inlined at build from `next.config` `env` from `SENTRY_DSN`).
 * Do not read non-public env vars here — they are stripped from client bundles.
 */
export function getBrowserSentryDsn(): string | undefined {
  const dsn = process.env.NEXT_PUBLIC_SENTRY_DSN?.trim()
  return dsn || undefined
}

function devSentryExplicitlyEnabled(): boolean {
  return (
    process.env.SENTRY_ENABLE_IN_DEV === "1" ||
    process.env.NEXT_PUBLIC_SENTRY_ENABLE_IN_DEV === "1"
  )
}

/**
 * When `NODE_ENV === "development"`, reporting is off unless `SENTRY_ENABLE_IN_DEV=1` (or `NEXT_PUBLIC_…`, mapped in next.config).
 * Test runs never send. Production/preview send when DSN is set.
 */
export function isSentryRuntimeEnabled(dsn: string | undefined): boolean {
  if (!dsn) return false
  if (process.env.NODE_ENV === "test" || process.env.VITEST) return false
  if (process.env.NODE_ENV === "development") {
    return devSentryExplicitlyEnabled()
  }
  return true
}

export function getSentryEnvironment(): string {
  return (
    process.env.SENTRY_ENVIRONMENT?.trim() ||
    process.env.VERCEL_ENV ||
    process.env.NODE_ENV ||
    "development"
  )
}

/** 0–1; invalid or missing env → 0 (no performance noise by default). */
export function getTracesSampleRate(): number {
  const raw = process.env.SENTRY_TRACES_SAMPLE_RATE
  const n = Number(raw ?? 0)
  if (!Number.isFinite(n) || n < 0 || n > 1) return 0
  return n
}

/** Browser replay sampling kept conservative by default for solo-operator noise control. */
export function getReplaySessionSampleRate(): number {
  const raw = process.env.SENTRY_REPLAY_SESSION_SAMPLE_RATE
  const n = Number(raw ?? 0)
  if (!Number.isFinite(n) || n < 0 || n > 1) return 0
  return n
}

/** Replay-on-error can be higher than baseline session replay, but still conservative. */
export function getReplayOnErrorSampleRate(): number {
  const raw = process.env.SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE
  const n = Number(raw ?? 0.1)
  if (!Number.isFinite(n) || n < 0 || n > 1) return 0.1
  return n
}

export function getSentryRelease(): string | undefined {
  const r =
    process.env.NEXT_PUBLIC_SENTRY_RELEASE?.trim() ||
    process.env.SENTRY_RELEASE?.trim() ||
    process.env.VERCEL_GIT_COMMIT_SHA?.trim()
  return r || undefined
}

type EventWithRequest = {
  request?: {
    cookies?: unknown
    headers?: Record<string, unknown> | null
    url?: string
  }
  message?: string
  exception?: { values?: Array<{ type?: string; value?: string }> }
  breadcrumbs?: Array<{ message?: string; data?: Record<string, unknown> }>
  user?: Record<string, unknown>
  extra?: Record<string, unknown>
  contexts?: Record<string, unknown>
}

/**
 * Drop cookies and sensitive headers from the event request (server + edge + browser).
 */
export function stripRequestPii<E extends EventWithRequest>(event: E): E | null {
  if (event.request) {
    delete event.request.cookies
    if (typeof event.request.url === "string") {
      event.request.url = redactSensitiveUrl(event.request.url)
    }
    const h = event.request.headers
    if (h && typeof h === "object") {
      const next: Record<string, string> = {}
      for (const [k, v] of Object.entries(h)) {
        const key = k.toLowerCase()
        if (
          key === "authorization" ||
          key === "cookie" ||
          key === "proxy-authorization" ||
          key === "stripe-signature" ||
          key === "webhook-signature" ||
          key === "x-api-key" ||
          key === "x-auth-token" ||
          key === "x-clerk-auth-reason" ||
          key === "cf-access-jwt-assertion"
        ) {
          continue
        }
        if (typeof v === "string") next[k] = v
      }
      event.request.headers = next
    }
  }
  scrubEventPayload(event)
  return event
}

function redactSensitiveUrl(url: string): string {
  try {
    const parsed = new URL(url)
    for (const key of [
      "token",
      "sig",
      "signature",
      "jwt",
      "access_token",
      "key",
      "invite",
      "inviteToken",
      "uploadSignature",
      "stripe_signature",
    ]) {
      if (parsed.searchParams.has(key)) {
        parsed.searchParams.set(key, "[redacted]")
      }
    }
    return parsed.toString()
  } catch {
    return url.replace(
      /([?&])(token|sig|signature|jwt|access[_-]?token|key|invite|invitetoken|uploadsignature|stripe_signature)=([^&\s]+)/gi,
      "$1$2=[redacted]",
    )
  }
}

function scrubText(value: string): string {
  return value
    .replace(
      /([?&])(token|sig|signature|jwt|access[_-]?token|key|invite|invitetoken|uploadsignature|stripe_signature)=([^&\s]+)/gi,
      "$1$2=[redacted]",
    )
    .replace(/\b(sk_(test|live)_[A-Za-z0-9]+)\b/g, "[redacted_stripe_key]")
    .replace(/\b(whsec_[A-Za-z0-9]+)\b/g, "[redacted_webhook_secret]")
}

function scrubValue(value: unknown): unknown {
  if (typeof value === "string") return scrubText(value)
  if (!value || typeof value !== "object") return value
  if (Array.isArray(value)) return value.map((item) => scrubValue(item))
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value)) {
    const key = k.toLowerCase()
    if (
      key.includes("token") ||
      key.includes("secret") ||
      key.includes("signature") ||
      key.includes("password") ||
      key.includes("cookie") ||
      key.includes("authorization")
    ) {
      continue
    }
    out[k] = scrubValue(v)
  }
  return out
}

function scrubEventPayload<E extends EventWithRequest>(event: E): void {
  if (event.message) event.message = scrubText(event.message)
  if (event.exception?.values) {
    event.exception.values = event.exception.values.map((v) => ({
      ...v,
      value: typeof v.value === "string" ? scrubText(v.value) : v.value,
    }))
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((b) => ({
      ...b,
      message: typeof b.message === "string" ? scrubText(b.message) : b.message,
      data: b.data ? (scrubValue(b.data) as Record<string, unknown>) : b.data,
    }))
  }
  if (event.user) {
    delete event.user.email
    delete event.user.ip_address
    delete event.user.username
  }
  if (event.extra) event.extra = scrubValue(event.extra) as Record<string, unknown>
  if (event.contexts) event.contexts = scrubValue(event.contexts) as Record<string, unknown>
}

/** Drop expected low-value browser noise to keep production issue volume actionable. */
export function shouldDropNoisySentryEvent<E extends EventWithRequest>(event: E): boolean {
  const message = event.message ?? event.exception?.values?.[0]?.value ?? ""
  const lower = message.toLowerCase()
  const dropMessagePatterns = [
    "clerk: development keys are for local development",
    "clerk loaded with development instance",
    "navigation cancelled",
    "nextrouter was not mounted",
    "aborterror",
    "network request failed",
    "failed to fetch dynamically imported module",
  ]
  if (dropMessagePatterns.some((p) => lower.includes(p))) return true

  if (lower.includes("redirect") && lower.includes("clerk")) return true
  return false
}
